import type { FC } from 'react'
import { HelpCircle } from '@untitled-ui/icons-react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import SvgIcon from '@mui/material/SvgIcon'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useNavigate } from 'react-router-dom'
import useTranslate from '@/hooks/use-translate'
import { paths } from '@/paths'
import { SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX } from './side-nav-item'
import {
  SIDE_NAV_ITEM_FONT_SIZE,
  sideNavButtonHoverSx,
} from './side-nav-layout-dimensions'
import { densityPx, densityPxStr } from '@/theme/base/density'

type SideNavHelpEntryProps = {
  isFolded: boolean
  showText: boolean
}

export const SideNavHelpEntry: FC<SideNavHelpEntryProps> = ({
  isFolded,
  showText,
}) => {
  const { t: tCommon } = useTranslate('common')
  const navigate = useNavigate()
  const helpPath = paths.dashboard.settings.support.help

  return (
    <Tooltip
      disableHoverListener={!isFolded}
      enterDelay={300}
      placement="right"
      title={tCommon('helpAndSupport')}
    >
      <Box sx={{ display: 'block', width: '100%' }}>
        <ButtonBase
          onClick={() => navigate(helpPath)}
          aria-label={isFolded ? tCommon('helpAndSupport') : undefined}
          sx={{
            alignItems: 'center',
            borderLeft: `${densityPx(3)}px solid transparent`,
            borderRadius: 1,
            display: 'flex',
            gap: !isFolded ? 1 : 0,
            justifyContent: 'flex-start',
            pl: `${SIDE_NAV_ICON_COLUMN_PADDING_LEFT_PX - densityPx(3)}px`,
            pr: densityPxStr(16),
            height: densityPxStr(40),
            textAlign: 'left',
            width: '100%',
            backgroundColor: 'transparent',
            '&:hover': sideNavButtonHoverSx,
          }}
        >
          <Box
            component="span"
            sx={{
              alignItems: 'center',
              color: 'var(--nav-item-icon-color)',
              display: 'inline-flex',
              flex: '0 0 auto',
              height: densityPxStr(28),
              justifyContent: 'center',
            }}
          >
            <SvgIcon fontSize="small">
              <HelpCircle fontSize="small" />
            </SvgIcon>
          </Box>
          {!isFolded && (
            <Typography
              sx={{
                color: 'var(--nav-item-color)',
                flexGrow: 1,
                fontFamily: (theme) => theme.typography.fontFamily,
                fontSize: SIDE_NAV_ITEM_FONT_SIZE,
                fontWeight: 300,
                lineHeight: densityPxStr(28),
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                marginLeft: densityPxStr(3),
                opacity: showText ? 1 : 0,
                transform: showText
                  ? 'translateX(0)'
                  : `translateX(-${densityPx(10)}px)`,
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                pointerEvents: showText ? 'auto' : 'none',
              }}
            >
              {tCommon('helpAndSupport')}
            </Typography>
          )}
        </ButtonBase>
      </Box>
    </Tooltip>
  )
}
